import { step, type JumpEngineInput } from './core.js';
import { jumpPressCandidates } from './reachability.js';
import {
  surfaceSegmentAt,
  type JumpEngineConfig,
  type JumpEngineState,
  type JumpHazard,
} from './state.js';

/** Beats past a hazard the planner sims to, so its clearing pass is committed. */
const COMMIT_LOOKAHEAD = 1e-3;

/**
 * One jump shape the planner tries per hazard. `launchVelocity` seeds the
 * analytic reachable window; `holdBeats` is how long after the press the `up`
 * fires, which decides the realised height in the engine:
 *   - LONG: release AT the long apex (`jumpVelocity / gravity`). At the apex the
 *     short-arc clamp is a no-op, so the cube keeps the full long arc. The apex
 *     is always before any landing, so the release never triggers an auto-hop.
 *   - SHORT: release early, while the cube is still low on its ascent, so the
 *     clamp drops it onto the short arc (apex `shortJumpVelocity² / (2·gravity)`).
 */
interface JumpProfile {
  launchVelocity: number;
  holdBeats: number;
}

/**
 * The two jump shapes, long first (max clearance — preferred when nothing caps
 * the height). A hazard pinned under a ceiling fails the long trial in the
 * engine (the apex cracks the overhang) and is cleared by the short trial.
 */
function jumpProfiles(config: JumpEngineConfig): JumpProfile[] {
  const apexLongBeats = config.jumpVelocity / config.gravity;
  // Beats for the long arc to climb to the SHORT apex height; release at half
  // that keeps the cube comfortably below it, so the clamp lands on the short
  // arc. `vLong² − 2g·shortPeak = vLong² − vShort²`.
  const toShortPeakBeats =
    (config.jumpVelocity -
      Math.sqrt(
        Math.max(
          0,
          config.jumpVelocity * config.jumpVelocity -
            config.shortJumpVelocity * config.shortJumpVelocity,
        ),
      )) /
    config.gravity;
  return [
    { launchVelocity: config.jumpVelocity, holdBeats: apexLongBeats },
    { launchVelocity: config.shortJumpVelocity, holdBeats: toShortPeakBeats * 0.5 },
  ];
}

/** Deep-clone an engine state (the Set + array fields need fresh copies). */
export function cloneJumpState(state: JumpEngineState): JumpEngineState {
  return {
    ...state,
    passedHazardIds: new Set(state.passedHazardIds),
    resolutions: [...state.resolutions],
  };
}

/**
 * Advance an engine state from `fromBeat` to `toBeat`, feeding `inputs` in
 * their beat windows. Shared by the replay runner (server + client re-sim)
 * and the planner's candidate verification, so both drive the identical
 * `step()` loop. Mutates `state`.
 */
export function advanceJumpSim(
  config: JumpEngineConfig,
  state: JumpEngineState,
  fromBeat: number,
  toBeat: number,
  inputs: JumpEngineInput[],
  stepBeats: number,
): void {
  const sorted = [...inputs].sort((a, b) => a.beat - b.beat);
  let idx = 0;
  while (idx < sorted.length && sorted[idx].beat < fromBeat - 1e-9) {
    idx += 1;
  }
  let cursor = fromBeat;
  while (cursor < toBeat - 1e-9 && state.alive && !state.completed) {
    const tickEnd = Math.min(cursor + stepBeats, toBeat);
    const tickInputs: JumpEngineInput[] = [];
    while (idx < sorted.length && sorted[idx].beat <= tickEnd + 1e-9) {
      tickInputs.push(sorted[idx]);
      idx += 1;
    }
    step({ config, state, fromBeat: cursor, toBeat: tickEnd, inputs: tickInputs });
    cursor = tickEnd;
  }
}

export interface PlannedJump {
  press: number;
  release: number;
  clearance: number;
}

/**
 * Non-lethal launch-surface samples to seed the analytic window from. The cube
 * launches from wherever it is grounded at the press, which is one of: where it
 * stands now (`baseState.beat`) or the ground just before the hazard's apex lead
 * (the common case after walking off a platform). Pit segments are skipped — you
 * never launch from inside a chasm; the OTHER sample (solid run-up) carries a
 * pit's far-ledge jump.
 */
function launchTopYSamples(
  config: JumpEngineConfig,
  fromBeat: number,
  hazardBeat: number,
  apexLead: number,
): number[] {
  const out: number[] = [];
  const pushAt = (beat: number): void => {
    const seg = surfaceSegmentAt(config.surface, Math.max(beat, fromBeat));
    if (!seg.lethal && !out.includes(seg.topY)) {
      out.push(seg.topY);
    }
  };
  pushAt(fromBeat);
  pushAt(hazardBeat - apexLead);
  return out;
}

/**
 * Find a single jump (a `down` then a prompt `up`) that clears `hazard` from
 * `baseState`, verified through the real engine so surface/elevation/auto-jump,
 * ceiling, saw, and pit interactions are exact. Tries a LONG then a SHORT jump
 * profile, each seeded from a couple of launch-surface samples (handles a pit's
 * run-up + a post-platform ground launch); within each, the apex-aligned press
 * first (maximum clearance) then a fan across the analytic feasible window.
 * Picks the surviving press with the greatest clearance — so an unconstrained
 * hazard takes the roomy long jump, while one capped by a ceiling falls through
 * to the short jump that fits beneath. Returns null if nothing clears.
 */
export function findClearingJump(
  config: JumpEngineConfig,
  baseState: JumpEngineState,
  hazard: JumpHazard,
  stepBeats: number,
): PlannedJump | null {
  let best: PlannedJump | null = null;
  for (const profile of jumpProfiles(config)) {
    const apexLead = profile.launchVelocity / config.gravity;
    const triedPresses = new Set<number>();
    for (const launchTopY of launchTopYSamples(config, baseState.beat, hazard.beat, apexLead)) {
      const candidates = jumpPressCandidates(
        config,
        hazard,
        launchTopY,
        baseState.beat + 1e-6,
        profile.launchVelocity,
      );
      for (const press of candidates) {
        if (press < baseState.beat - 1e-9 || press >= hazard.beat) {
          continue;
        }
        const pressKey = Math.round(press * 1e6);
        if (triedPresses.has(pressKey)) {
          continue;
        }
        triedPresses.add(pressKey);
        const trial = cloneJumpState(baseState);
        const release = press + profile.holdBeats;
        // Sim past BOTH the hazard and the release so the `up` is always applied
        // — vital for the short profile (the release IS the height-cutting clamp)
        // and to leave a clean, un-pressed state for the clearance read-out.
        advanceJumpSim(
          config,
          trial,
          baseState.beat,
          Math.max(hazard.beat, release) + COMMIT_LOOKAHEAD,
          [
            { beat: press, type: 'down' },
            { beat: release, type: 'up' },
          ],
          stepBeats,
        );
        if (!trial.alive || !trial.passedHazardIds.has(hazard.id)) {
          continue;
        }
        const resolution = trial.resolutions.find((r) => r.obstacleId === hazard.id);
        const clearance = resolution ? resolution.clearance : 0;
        if (best === null || clearance > best.clearance) {
          best = { press, release, clearance };
        }
      }
    }
  }
  return best;
}
